import { z } from 'zod';

// Cross-category schedule search — one term across every schedule content
// kind, unbounded by the visible calendar window (every other schedule
// endpoint only fetches a from/to range). `workout` covers BOTH the
// `Schedule` rows whose `kind` resolves to 'workout' AND those that resolve
// to 'program' (the frontend's own `programId ? 'program' : 'workout'`
// split, mirrored server-side) — there is no separate 'program' filter value
// because the two share one source table and one query.
export const SCHEDULE_SEARCH_KINDS = [
  'workout',
  'note',
  'nutrition',
  'questionnaire',
  'booking',
] as const;

export type ScheduleSearchKind = (typeof SCHEDULE_SEARCH_KINDS)[number];

// A bare `?kinds=workout` arrives as a string, not a one-element array —
// Express/Nest only produces an array for a REPEATED key. Same normalization
// as `appliesTo` in `QueryAllDto`, plus a comma-split: the frontend sends
// multiple kinds as one `kinds=workout,booking` param rather than repeated
// keys, so a single string value must be split before landing on the enum.
const toArray = (value: unknown) => {
  if (value === undefined) return undefined;
  if (Array.isArray(value)) return value;
  return typeof value === 'string' ? value.split(',') : [value];
};

export const ListScheduleSearchQueryDto = z
  .object({
    q: z.string().trim().min(1).max(255),
    kinds: z
      .preprocess(toArray, z.array(z.enum(SCHEDULE_SEARCH_KINDS)).min(1))
      .optional(),
    page: z.coerce.number().int().min(1).optional().default(1),
    limit: z.coerce.number().int().min(1).max(100).optional().default(20),
  })
  .strict();

export type ApiListScheduleSearchQueryDto = z.infer<
  typeof ListScheduleSearchQueryDto
>;

// The org-wide mount adds the owner-only trainer/client narrowing —
// identical fields to the sibling org-wide schedule listings.
export const ListOrgScheduleSearchQueryDto = ListScheduleSearchQueryDto.extend({
  clientId: z.string().uuid().optional(),
  trainerId: z.string().uuid().optional(),
});

export type ApiListOrgScheduleSearchQueryDto = z.infer<
  typeof ListOrgScheduleSearchQueryDto
>;
